import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { Sparkles, PieChart as PieChartIcon } from 'lucide-react';
import TimeFilterControls, { TimeFilter } from './TimeFilterControls';

interface ValidationTimelineData {
  date: string;
  validated: number;
  score_le_3: number;
}

interface AIValidationData {
  total_validated: number;
  score_le_3_count: number;
  quality_percentage: number;
  average_score: number;
  score_distribution: {
    [key: string]: number;
  };
  timeline: ValidationTimelineData[];
}

const COLORS = {
  '1': '#10b981',
  '2': '#3b82f6',
  '3': '#f59e0b',
  '4': '#f97316',
  '5': '#ef4444',
};

const AIValidationAnalytics: React.FC = () => {
  const [data, setData] = useState<AIValidationData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [timeFilter, setTimeFilter] = useState<TimeFilter>({});
  const previousFilterRef = useRef<string>('');

  const fetchAIValidationStats = useCallback(async (filter: TimeFilter) => {
    try {
      setLoading(true);
      setError(null);

      const apiUrl = process.env.REACT_APP_API_URL || '';
      const token = localStorage.getItem('auth_token');
      const headers: HeadersInit = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      const params = new URLSearchParams();
      if (filter.limitDays) {
        params.append('limit_days', filter.limitDays.toString());
      }
      if (filter.startDate) {
        params.append('start_date', filter.startDate);
      }
      if (filter.endDate) {
        params.append('end_date', filter.endDate);
      }

      const url = `${apiUrl}/api/admin/analytics/ai-validation/stats?${params.toString()}`;
      const response = await fetch(url, { headers });

      if (!response.ok) {
        throw new Error(`Failed to fetch AI validation stats: ${response.status}`);
      }

      const statsData = await response.json();
      setData(statsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch AI validation stats');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const filterKey = JSON.stringify(timeFilter);
    if (filterKey !== previousFilterRef.current) {
      previousFilterRef.current = filterKey;
      fetchAIValidationStats(timeFilter);
    }
  }, [timeFilter, fetchAIValidationStats]);

  const handleFilterChange = useCallback((filter: TimeFilter) => {
    setTimeFilter(filter);
  }, []);

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          <span className="ml-3 text-gray-600">Loading AI validation analytics...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg border border-red-200 p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-600">Error: {error}</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <TimeFilterControls onFilterChange={handleFilterChange} defaultFilter={timeFilter} />
        <div className="flex flex-col items-center justify-center h-64 text-gray-500">
          <PieChartIcon className="h-12 w-12 mb-4" />
          <p className="text-lg font-medium">No AI validation data available</p>
          <p className="text-sm">Check back once validations are performed</p>
        </div>
      </div>
    );
  }

  const processedTimelineData = data.timeline.map((item) => ({
    ...item,
    formattedDate: new Date(item.date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    }),
  }));

  const pieChartData = Object.entries(data.score_distribution).map(([score, count]) => ({
    name: `Score ${score}`,
    value: count,
    score,
  }));

  const barChartData = Object.entries(data.score_distribution).map(([score, count]) => ({
    score: `Score ${score}`,
    count,
  }));

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-medium text-gray-900">
            {new Date(data.date).toLocaleDateString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </p>
          <p className="text-indigo-600">
            <span className="font-medium">{data.validated}</span> persons validated
          </p>
          <p className="text-green-600">
            <span className="font-medium">{data.score_le_3}</span> with score ≤ 3 (good quality)
          </p>
        </div>
      );
    }
    return null;
  };

  const PieTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-medium text-gray-900">{data.name}</p>
          <p className="text-indigo-600">
            <span className="font-medium">{data.value}</span> persons
          </p>
          <p className="text-gray-500 text-sm">
            {data.value > 0
              ? `${((data.value / data.total_validated) * 100).toFixed(1)}% of total`
              : '0%'}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      <TimeFilterControls onFilterChange={handleFilterChange} defaultFilter={timeFilter} />

      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <Sparkles className="h-6 w-6 text-indigo-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">AI Validation Analytics</h3>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-indigo-50 rounded-lg p-4">
            <div className="text-sm font-medium text-indigo-600">Total Validated</div>
            <div className="text-2xl font-bold text-indigo-900">{data.total_validated}</div>
          </div>
          <div className="bg-green-50 rounded-lg p-4">
            <div className="text-sm font-medium text-green-600">Score ≤ 3 (Good Quality)</div>
            <div className="text-2xl font-bold text-green-900">{data.score_le_3_count}</div>
          </div>
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="text-sm font-medium text-blue-600">Quality Percentage</div>
            <div className="text-2xl font-bold text-blue-900">{data.quality_percentage}%</div>
          </div>
          <div className="bg-amber-50 rounded-lg p-4">
            <div className="text-sm font-medium text-amber-600">Average Score</div>
            <div className="text-2xl font-bold text-amber-900">{data.average_score}</div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div>
            <h4 className="text-md font-medium text-gray-900 mb-4">Score Distribution (Pie Chart)</h4>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieChartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) =>
                      percent && percent > 0.05 ? `${name}: ${(percent * 100).toFixed(1)}%` : ''
                    }
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {pieChartData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[entry.score as keyof typeof COLORS] || '#8884d8'}
                      />
                    ))}
                  </Pie>
                  <Tooltip content={<PieTooltip />} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div>
            <h4 className="text-md font-medium text-gray-900 mb-4">Score Distribution (Bar Chart)</h4>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={barChartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="score"
                    stroke="#6b7280"
                    fontSize={12}
                    tick={{ fill: '#6b7280' }}
                  />
                  <YAxis
                    stroke="#6b7280"
                    fontSize={12}
                    tick={{ fill: '#6b7280' }}
                    allowDecimals={false}
                  />
                  <Tooltip />
                  <Bar
                    dataKey="count"
                    radius={[4, 4, 0, 0]}
                  >
                    {barChartData.map((entry, index) => {
                      const score = entry.score.replace('Score ', '');
                      return (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[score as keyof typeof COLORS] || '#8884d8'}
                        />
                      );
                    })}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {data.timeline && data.timeline.length > 0 && (
          <div>
            <h4 className="text-md font-medium text-gray-900 mb-4">Validation Timeline</h4>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={processedTimelineData}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="formattedDate"
                    stroke="#6b7280"
                    fontSize={12}
                    tick={{ fill: '#6b7280' }}
                  />
                  <YAxis
                    stroke="#6b7280"
                    fontSize={12}
                    tick={{ fill: '#6b7280' }}
                    allowDecimals={false}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="validated"
                    stroke="#4f46e5"
                    strokeWidth={2}
                    name="Total Validated"
                    dot={{ fill: '#4f46e5', strokeWidth: 2, r: 3 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="score_le_3"
                    stroke="#10b981"
                    strokeWidth={2}
                    name="Score ≤ 3 (Good Quality)"
                    dot={{ fill: '#10b981', strokeWidth: 2, r: 3 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIValidationAnalytics;

